/* District Bangle — facets.js
   Enhances the native collection filter/sort form to fetch-and-swap via the
   Section Rendering API. With JS off, the form submits normally (real GET with
   filter params) — this only removes the full reload. Updates history so back/
   forward work, and keeps the results region's aria-live announcement.

   Two contracts this file depends on, both marked in the Liquid so a rename
   cannot silently break the swap:
     [data-facets-root][data-section-id]  the section, and its id for the
                                          Section Rendering API. Read it from
                                          the attribute, never from element.id
                                          — the rendered section carries no id.
     [data-facet-form] [data-facet-panel] [data-facet-results]
                                          the form to serialise and the two
                                          regions to replace.
   Listeners are delegated from the custom element, so replacing either region
   never leaves a dead handler behind. */
import { BaseElement, define } from '@theme/global';

/* Only facet state belongs in the URL. Anything else that reaches the form —
   a product card's hidden inputs, an app's stray field — is dropped rather
   than trusted, so a markup regression degrades to "no filter" instead of
   submitting form_type=product to the collection route. */
const FACET_PARAM = /^(filter\.|sort_by$|q$|page$|section_id$)/;

class FacetFilters extends BaseElement {
  init() {
    this.root = this.closest('[data-facets-root]');
    if (!this.root) return;
    this.sectionId = this.root.dataset.sectionId || '';
    if (!this.sectionId) return; // no id, no Section Rendering — leave the native form alone

    this.listen(this, 'change', (e) => { if (this.isFacetControl(e.target)) this.apply(); });
    this.listen(this, 'input', (e) => {
      if (e.target.type !== 'number' || !this.isFacetControl(e.target)) return;
      clearTimeout(this._t);
      this._t = setTimeout(() => this.apply(), 500);
    });
    this.listen(this, 'submit', (e) => { e.preventDefault(); this.apply(); });
    // The chip/clear/remove links are real hrefs; intercept them so they swap
    // in place, and let a modified click through to the browser.
    this.listen(this, 'click', (e) => {
      const a = e.target.closest('[data-facet-link]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      const url = new URL(a.href, window.location.origin);
      this.render(url.search || '?', true);
    });
    this.listen(window, 'popstate', () => this.render(window.location.search, false));
  }

  get form() {
    return this.querySelector('[data-facet-form]') || this.root.querySelector('[data-facet-form]');
  }

  isFacetControl(el) {
    return !!(el && el.name && FACET_PARAM.test(el.name));
  }

  apply() {
    const form = this.form;
    if (!form) return;
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (v === '' || !FACET_PARAM.test(k)) continue;
      params.append(k, v);
    }
    params.delete('section_id');
    this.render(`?${params.toString()}`, true);
  }

  async render(search, push) {
    this.setAttribute('aria-busy', 'true');
    try {
      const qs = search && search !== '?' ? search : '';
      const url = `${window.location.pathname}${qs}`;
      const res = await fetch(`${url}${qs ? '&' : '?'}section_id=${this.sectionId}`);
      if (!res.ok) throw new Error(`section render ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-facets-root]');
      if (!fresh) throw new Error('no [data-facets-root] in the rendered section');
      let swapped = 0;
      for (const hook of ['[data-facet-results]', '[data-facet-panel]']) {
        const next = fresh.querySelector(hook);
        const current = this.root.querySelector(hook);
        if (next && current) { current.replaceWith(next); swapped += 1; }
      }
      if (!swapped) throw new Error('nothing matched the swap hooks');
      if (push) window.history.pushState({}, '', url);
    } catch (e) {
      // A silent no-op is the worse failure: filters that "do nothing" read as
      // broken. Fall back to a real navigation so the filter still applies.
      console.error('facets', e);
      window.location.search = search;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/* Load more / infinite scroll — the merchant's Pagination setting, read off the
   grid's [data-pagination]. `paged` never reaches here: the Liquid renders the
   numbered nav on its own and this element is not emitted at all.

   The numbered links are always in the markup — crawlable, and the whole of the
   experience with JS off — so this element hides them only once it has taken
   the job over, and hands them straight back if a fetch ever fails. Pages come
   from the Section Rendering API, so an appended card is built by the same
   Liquid that built the first page rather than by a second renderer in JS. */
class LoadMore extends BaseElement {
  init() {
    this.root = this.closest('[data-facets-root]');
    this.grid = this.root && this.root.querySelector('[data-card-grid]');
    if (!this.grid) return;
    this.sectionId = this.root.dataset.sectionId || '';
    this.mode = this.grid.dataset.pagination || 'paged';
    if (!this.sectionId || this.mode === 'paged') return; // leave the numbered nav alone

    this.nav = this.querySelector('.pagination');
    this.button = this.querySelector('[data-load-more]');
    if (this.nav) this.nav.hidden = true;
    if (this.button) {
      this.listen(this.button, 'click', () => this.load());
      this.button.hidden = this.mode === 'infinite';
    }
    if (this.mode === 'infinite') this.watch();
  }

  watch() {
    this.observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) this.load(); },
      { rootMargin: '400px' });
    this.observer.observe(this);
    this.onCleanup(() => this.stop());
  }

  stop() {
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
  }

  exhausted() {
    if (this.button) this.button.hidden = true;
    this.stop();
  }

  async load() {
    if (this.busy || !this.dataset.next) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(this.dataset.next, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`section render ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-card-grid]');
      if (!fresh) throw new Error('no [data-card-grid] in the rendered page');
      this.grid.append(...fresh.children);
      const next = doc.querySelector('load-more');
      if (next && next.dataset.next) this.dataset.next = next.dataset.next;
      else { delete this.dataset.next; this.exhausted(); }
    } catch (e) {
      // A button that silently stops working reads as broken. Give the real
      // page links back instead — they were never removed, only hidden.
      console.error('load more', e);
      if (this.nav) this.nav.hidden = false;
      if (this.button) this.button.hidden = true;
      this.stop();
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

define('facet-filters', FacetFilters);
define('load-more', LoadMore);
